import validator from 'validator';
import { securityConfig } from '../config/security.config';

/**
 * Input Validation Utilities
 * Validates user input according to security policies
 */

class Validator {
  /**
   * Comprehensive email validation regex (RFC 5322 simplified)
   * Matches: username@domain.tld
   * - Local part: alphanumeric, dot, hyphen, underscore, plus sign
   * - Domain: alphanumeric, dot, hyphen
   * - TLD: 2-6 characters
   */
  static EMAIL_REGEX = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  /**
   * Email validation without plus sign (for registration)
   * Prevents email aliasing/subaddressing
   */
  static EMAIL_REGEX_NO_PLUS = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  /**
   * Validate email format for registration (strict - no + sign allowed)
   * Use this for new user registration to prevent email aliasing
   */
  static isValidEmailForRegistration(email) {
    if (!email || typeof email !== 'string') return false;
    
    // Trim whitespace
    email = email.trim();
    
    // Check length (RFC 5321)
    if (email.length > 254) return false;
    
    // Validate with regex (no + sign)
    if (!this.EMAIL_REGEX_NO_PLUS.test(email)) return false;
    
    // Check for consecutive dots
    if (email.includes('..')) return false;
    
    // Check if local part starts or ends with dot
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) return false;
    if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
    
    // Check domain has at least one dot
    if (!domain.includes('.')) return false;
    
    // Check domain doesn't start or end with dot or hyphen
    if (domain.startsWith('.') || domain.endsWith('.') || 
        domain.startsWith('-') || domain.endsWith('-')) return false;
    
    return true;
  }

  /**
   * Validate email format for login (allows + sign)
   * Use this for existing users who may have registered with + sign
   */
  static isValidEmailForLogin(email) {
    if (!email || typeof email !== 'string') return false;
    
    // Trim whitespace
    email = email.trim().toLowerCase();
    
    // Check length (RFC 5321)
    if (email.length > 254) return false;
    
    // Validate with regex (allows + sign)
    if (!this.EMAIL_REGEX.test(email)) return false;
    
    // Check for consecutive dots
    if (email.includes('..')) return false;
    
    // Check if local part starts or ends with dot
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) return false;
    if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
    
    // Check domain has at least one dot
    if (!domain.includes('.')) return false;
    
    // Check domain doesn't start or end with dot or hyphen
    if (domain.startsWith('.') || domain.endsWith('.') || 
        domain.startsWith('-') || domain.endsWith('-')) return false;
    
    return true;
  }

  /**
   * Validate email format (legacy method - uses strict validation)
   * @deprecated Use isValidEmailForRegistration or isValidEmailForLogin instead
   */
  static isValidEmail(email) {
    return this.isValidEmailForRegistration(email);
  }

  /**
   * Validate password strength
   * Returns error code that can be translated in the component
   */
  static isValidPassword(password) {
    if (!password || typeof password !== 'string') {
      return { valid: false, errorCode: 'passwordRequired' };
    }

    const { minLength, requireUppercase, requireLowercase, requireNumbers, requireSpecialChars } = 
      securityConfig.password;

    if (password.length < minLength) {
      return { valid: false, errorCode: 'passwordMinLength' };
    }

    if (requireUppercase && !/[A-Z]/.test(password)) {
      return { valid: false, errorCode: 'passwordUppercase' };
    }

    if (requireLowercase && !/[a-z]/.test(password)) {
      return { valid: false, errorCode: 'passwordLowercase' };
    }

    if (requireNumbers && !/\d/.test(password)) {
      return { valid: false, errorCode: 'passwordNumber' };
    }

    if (requireSpecialChars && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      return { valid: false, errorCode: 'passwordSpecialChar' };
    }

    // Check for common passwords
    const commonPasswords = ['password', '12345678', 'qwerty', 'abc123', 'password123'];
    if (commonPasswords.includes(password.toLowerCase())) {
      return { valid: false, errorCode: 'passwordTooCommon' };
    }

    return { valid: true };
  }

  /**
   * Validate URL
   */
  static isValidURL(url) {
    if (!url || typeof url !== 'string') return false;
    
    return validator.isURL(url, {
      protocols: ['http', 'https'],
      require_protocol: true,
      require_valid_protocol: true,
      allow_query_components: true,
      validate_length: true,
    });
  }

  /**
   * Validate phone number
   */
  static isValidPhone(phone, locale = 'any') {
    if (!phone || typeof phone !== 'string') return false;
    return validator.isMobilePhone(phone, locale);
  }

  /**
   * Validate input length
   */
  static isValidLength(input, min = 0, max = securityConfig.validation.maxInputLength) {
    if (!input) return min === 0;
    const length = String(input).length;
    return length >= min && length <= max;
  }

  /**
   * Validate alphanumeric
   */
  static isAlphanumeric(input) {
    if (!input || typeof input !== 'string') return false;
    return validator.isAlphanumeric(input);
  }

  /**
   * Validate numeric
   */
  static isNumeric(input) {
    if (input === null || input === undefined) return false;
    return validator.isNumeric(String(input));
  }

  /**
   * Validate date
   */
  static isValidDate(date) {
    if (!date) return false;
    return validator.isDate(String(date));
  }

  /**
   * Validate file type
   */
  static isValidFileType(mimeType) {
    if (!mimeType) return false;
    return securityConfig.validation.allowedFileTypes.includes(mimeType);
  }

  /**
   * Validate file size
   */
  static isValidFileSize(size) {
    if (!size || isNaN(size)) return false;
    return size <= securityConfig.validation.maxFileSize;
  }

  /**
   * Validate UUID
   */
  static isValidUUID(uuid) {
    if (!uuid || typeof uuid !== 'string') return false;
    return validator.isUUID(uuid);
  }

  /**
   * Validate JSON
   */
  static isValidJSON(jsonString) {
    if (!jsonString || typeof jsonString !== 'string') return false;
    try {
      JSON.parse(jsonString);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Validate credit card (if needed)
   */
  static isValidCreditCard(cardNumber) {
    if (!cardNumber || typeof cardNumber !== 'string') return false;
    return validator.isCreditCard(cardNumber);
  }

  /**
   * Validate IP address
   */
  static isValidIP(ip, version = 4) {
    if (!ip || typeof ip !== 'string') return false;
    return validator.isIP(ip, version);
  }

  /**
   * Check for SQL injection patterns
   */
  static hasSQLInjection(input) {
    if (!input || typeof input !== 'string') return false;
    
    const sqlPatterns = [
      /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|UNION|DECLARE)\b)/i,
      /(--|;|\/\*|\*\/|xp_|sp_)/i,
      /('|(--)|;|\/\*|\*\/|\bOR\b|\bAND\b)/i,
    ];
    
    return sqlPatterns.some(pattern => pattern.test(input));
  }

  /**
   * Check for XSS patterns
   */
  static hasXSS(input) {
    if (!input || typeof input !== 'string') return false;
    
    const xssPatterns = [
      /<script[^>]*>.*?<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /<iframe/gi,
      /<object/gi,
      /<embed/gi,
    ];
    
    return xssPatterns.some(pattern => pattern.test(input));
  }

  /**
   * Check for path traversal
   */
  static hasPathTraversal(input) {
    if (!input || typeof input !== 'string') return false;
    
    const pathTraversalPatterns = [
      /\.\./,
      /\.\\/,
      /\.\/\./,
      /%2e%2e/i,
      /%252e%252e/i,
    ];
    
    return pathTraversalPatterns.some(pattern => pattern.test(input));
  }

  /**
   * Comprehensive validation
   */
  static validate(input, rules = {}) {
    const errors = [];
    
    if (rules.required && (!input || String(input).trim() === '')) {
      errors.push('This field is required');
      return { valid: false, errors };
    }
    
    if (!input && !rules.required) {
      return { valid: true, errors: [] };
    }
    
    if (rules.type) {
      switch (rules.type) {
        case 'email':
          if (!this.isValidEmail(input)) {
            errors.push('Invalid email format');
          }
          break;
        case 'url':
          if (!this.isValidURL(input)) {
            errors.push('Invalid URL format');
          }
          break;
        case 'phone':
          if (!this.isValidPhone(input)) {
            errors.push('Invalid phone number');
          }
          break;
        case 'numeric':
          if (!this.isNumeric(input)) {
            errors.push('Must be a number');
          }
          break;
        case 'alphanumeric':
          if (!this.isAlphanumeric(input)) {
            errors.push('Must contain only letters and numbers');
          }
          break;
        default:
          // No validation for unknown types
          break;
      }
    }
    
    if (rules.minLength || rules.maxLength) {
      if (!this.isValidLength(input, rules.minLength, rules.maxLength)) {
        errors.push(`Length must be between ${rules.minLength || 0} and ${rules.maxLength || 'unlimited'}`);
      }
    }
    
    if (rules.checkXSS && this.hasXSS(input)) {
      errors.push('Input contains potentially dangerous content');
    }
    
    if (rules.checkSQLInjection && this.hasSQLInjection(input)) {
      errors.push('Input contains potentially dangerous SQL patterns');
    }
    
    if (rules.checkPathTraversal && this.hasPathTraversal(input)) {
      errors.push('Input contains potentially dangerous path patterns');
    }
    
    if (rules.pattern && !rules.pattern.test(input)) {
      errors.push(rules.patternMessage || 'Invalid format');
    }
    
    return {
      valid: errors.length === 0,
      errors,
    };
  }
}

export default Validator;
